import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Container, Box, Typography, CircularProgress, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useStoreInfo } from '@/hooks/useStoreInfo';
import { useStoreTracking } from '@/hooks/useStoreTracking';
import { useDomain } from '@/hooks/useDomain';
import { ReservationFooter } from '@/components/ReservationFooter';
import { WidgetThemeProvider, useWidgetTheme } from '@/context/WidgetThemeContext';
import { useEmbed } from '@/context/EmbedContext';
import { configureStoreTracking, setStoreDomain } from '@/analytics/ga';
import type { ReservationStoreInfoResponseDto } from '@/api/generated';

function BackgroundLayer() {
  const { backgroundImageUrl } = useWidgetTheme();

  if (!backgroundImageUrl) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `url(${backgroundImageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        zIndex: -1,
      }}
    />
  );
}

function StoreHeader({ storeName }: { storeName?: string }) {
  const { logoImageUrl, hasBackground } = useWidgetTheme();

  const textShadow = hasBackground ? '0 1px 4px rgba(0,0,0,0.5)' : undefined;

  return (
    <Box mb={4} textAlign="center">
      {logoImageUrl ? (
        <Box
          component="img"
          src={logoImageUrl}
          alt={storeName || 'Store Logo'}
          sx={{
            width: 300,
            height: 100,
            objectFit: 'contain',
            mb: 1,
          }}
        />
      ) : (
        storeName && (
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ textShadow }}
          >
            {storeName}
          </Typography>
        )
      )}
    </Box>
  );
}

function ReservationLayoutContent({ storeInfo }: { storeInfo: ReservationStoreInfoResponseDto }) {
  const { isEmbedded } = useEmbed();

  return (
    <>
      {/* Only show background when NOT embedded */}
      {!isEmbedded && <BackgroundLayer />}
      <Container maxWidth="md">
        <Box
          py={isEmbedded ? 2 : 4}
          sx={isEmbedded ? { backgroundColor: 'transparent' } : undefined}
        >
          {/* Only show header when NOT embedded */}
          {!isEmbedded && <StoreHeader storeName={storeInfo?.name} />}
          <Outlet context={{ storeInfo }} />
          <ReservationFooter
            reservationNotice={storeInfo?.widget_settings?.reservation_notice}
          />
        </Box>
      </Container>
    </>
  );
}

/**
 * Layout component that wraps all reservation pages
 * Loads store information and displays it at the top
 */
export function ReservationLayout() {
  const domain = useDomain();
  const { t } = useTranslation();
  const { data: storeInfo, isLoading, error } = useStoreInfo(domain);
  const { data: storeTracking } = useStoreTracking(domain);

  useEffect(() => {
    document.title = storeInfo?.name || 'SALA';
  }, [storeInfo?.name]);

  useEffect(() => {
    setStoreDomain(domain);
  }, [domain]);

  useEffect(() => {
    if (storeTracking) {
      configureStoreTracking(storeTracking);
    }
  }, [storeTracking]);

  if (isLoading) {
    return (
      <Container maxWidth="md">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Box mt={4}>
          <Alert severity="error">
            {t('reservationLayout.loadError')}
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <WidgetThemeProvider widgetSettings={storeInfo?.widget_settings}>
      <ReservationLayoutContent storeInfo={storeInfo!} />
    </WidgetThemeProvider>
  );
}
